import React, { useMemo, useRef } from 'react';
import { Opportunity as OpportunityConst } from '@axiom/const';
import {
  AsyncConnector,
  Button,
  CondensedLabel,
  Dropdown,
  Form,
  Gutter,
  Icon,
  Layout,
  ParagraphHeader,
  Well,
} from '@axiom/ui';
import type { Candidate, Opportunity } from '@axiom/validation';
import { AhqFindOpportunitySchema } from '@axiom/validation';

import type { ReadOpportunitiesResponseType } from '../../api/opportunity-api';
import { OpportunityApi } from '../../api/opportunity-api';
import { AddCandidatesToOpportunityUtil } from '../../utils/AddCandidatesToOpportunityUtil';

export type TalentProfileDetailOppMatchingProps = {
  name?: string;
  candidateId: Candidate['id'];
  candidateName?: string | null;
  onSuccess?: () => void;
};

export const TalentProfileDetailOppMatching = ({
  name = 'OPP_MATCHING_SECTION',
  candidateId,
  candidateName,
  onSuccess,
}: TalentProfileDetailOppMatchingProps) => {
  const parsedOpportunitiesRef = useRef<Array<{ id: string; name: string }>>(
    []
  );

  const opportunitiesConnector = useMemo(
    () =>
      new AsyncConnector<ReadOpportunitiesResponseType>({
        getData: args => {
          return OpportunityApi.readOpportunities({
            filters: {
              stageCode: [
                OpportunityConst.STAGE_CODE_MAP[OpportunityConst.DEVELOP],
                OpportunityConst.STAGE_CODE_MAP[OpportunityConst.CRAFT],
                OpportunityConst.STAGE_CODE_MAP[OpportunityConst.COMPETE],
                OpportunityConst.STAGE_CODE_MAP[OpportunityConst.CONTRACT],
              ],
              isFulfillmentActive: true,
            },
            search: args.search,
            sort: 'relevance',
            page: args.page,
          });
        },
        parseData: results => {
          const opportunities = results.data.map((opp: Opportunity) => ({
            id: opp.id,
            name: opp.jobName ?? '',
          }));
          parsedOpportunitiesRef.current = opportunities;

          return {
            currentPage: results.meta.currentPage,
            totalPages: results.meta.pageCount,
            data: opportunities,
          };
        },
      }),
    []
  );

  return (
    <Well background="light blue" name={name}>
      <Layout
        direction="horizontal"
        horizontalGutter="8px"
        position="left middle"
      >
        <Icon name="intelligence-filled" />

        <ParagraphHeader name="OPP_MATCHING_HEADER">
          Opp Matching
        </ParagraphHeader>
      </Layout>
      <Gutter top="16px">
        <Form
          key={candidateId}
          name="OPP_MATCHING_FORM"
          schema={AhqFindOpportunitySchema}
          initialValues={{
            opportunityId: undefined,
          }}
          onSubmit={async (formData, actions) => {
            const { opportunityId } = formData;
            if (opportunityId) {
              const opportunityName = parsedOpportunitiesRef.current.find(
                o => o.id === opportunityId
              )?.name;
              const success = await AddCandidatesToOpportunityUtil.submit({
                opportunityId,
                opportunityName,
                candidateIds: [candidateId],
                candidateName,
                reloadCandidateId: candidateId,
              });
              if (!success) return;

              actions.resetForm();
              onSuccess?.();
            }
          }}
        >
          {({ fireSubmit }) => (
            <>
              <CondensedLabel name="OPP_MATCHING_LABEL">
                Add this talent to an opp to see how well they match!
              </CondensedLabel>
              <Gutter top="8px" bottom="16px">
                <Dropdown
                  name="opportunityId"
                  options={opportunitiesConnector}
                  displayKey="name"
                  valueKey="id"
                  placeholder="Type here to start searching..."
                />
              </Gutter>
              <Button name="ADD_TO_TALENT_POOL" onClick={fireSubmit}>
                Add to Talent Pool
              </Button>
            </>
          )}
        </Form>
      </Gutter>
    </Well>
  );
};
